import React, { useState, useCallback, useEffect } from 'react';
import './App.css';
import { createClozeTest } from './utils/wordBlanking';

const STORAGE_KEY = 'cloze-test-texts';

interface AttemptData {
  score: number;
  total: number;
  date: string;
  isPerfect: boolean;
}

interface TextData {
  id: string;
  text: string;
  preview: string;
  createdAt: string;
  attempts: AttemptData[];
  attemptCount: number;
  perfectCount: number;
  lastAttemptDate: string;
  selectionMode: 'random' | 'manual';
  selectedWordIndices?: number[];
  lastResult?: {
    score: number;
    total: number;
    date: string;
  };
}

interface OriginalWord {
  index: number;
  word: string;
}

interface Result {
  original: string;
  userAnswer: string;
  isCorrect: boolean;
}

const saveTextToStorage = (text: string, results: Result[] | null = null, selectionMode: 'random' | 'manual' = 'random', selectedWordIndices?: number[]): string => {
  const existingTexts: TextData[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  const textId = Date.now().toString();
  const preview = text.substring(0, 50) + (text.length > 50 ? '...' : '');

  const textData: TextData = {
    id: textId,
    text,
    preview,
    createdAt: new Date().toISOString(),
    attempts: results ? [{
      score: results.filter(r => r.isCorrect).length,
      total: results.length,
      date: new Date().toISOString(),
      isPerfect: results.every(r => r.isCorrect)
    }] : [],
    attemptCount: results ? 1 : 0,
    perfectCount: results && results.every(r => r.isCorrect) ? 1 : 0,
    lastAttemptDate: new Date().toISOString(),
    selectionMode,
    selectedWordIndices
  };

  const updatedTexts = [textData, ...existingTexts].slice(0, 10); // Keep only last 10
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedTexts));
  return textId;
};

const updateTextResult = (textId: string, results: Result[]): void => {
  const existingTexts: TextData[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  const score = results.filter(r => r.isCorrect).length;
  const total = results.length;
  const isPerfect = score === total;
  const attemptData: AttemptData = {
    score,
    total,
    date: new Date().toISOString(),
    isPerfect
  };

  const updatedTexts = existingTexts.map(textData => {
    if (textData.id === textId) {
      const attempts = [...(textData.attempts || []), attemptData];
      const perfectCount = (textData.perfectCount || 0) + (isPerfect ? 1 : 0);
      const attemptCount = (textData.attemptCount || 0) + 1;

      return {
        ...textData,
        attempts,
        attemptCount,
        perfectCount,
        lastAttemptDate: attemptData.date
      };
    }
    return textData;
  }).filter(textData => {
    // Remove texts that have achieved 3 perfect scores
    return textData.perfectCount < 3;
  });

  localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedTexts));
};

const migrateOldData = (textData: any): TextData => {
  // If it's already in new format with all fields, return as is
  if (textData.attempts !== undefined && textData.attemptCount !== undefined && textData.selectionMode !== undefined) {
    return textData as TextData;
  }

  // If attempts exist but other fields are missing, add them
  if (textData.attempts !== undefined) {
    return {
      ...textData,
      attemptCount: textData.attemptCount ?? textData.attempts.length,
      selectionMode: textData.selectionMode ?? 'random'
    } as TextData;
  }

  // Migrate old format to new format
  const migratedData: TextData = {
    ...textData,
    attempts: textData.lastResult ? [{
      score: textData.lastResult.score,
      total: textData.lastResult.total,
      date: textData.lastResult.date,
      isPerfect: textData.lastResult.score === textData.lastResult.total
    }] : [],
    attemptCount: textData.lastResult ? 1 : 0,
    perfectCount: (textData.lastResult && textData.lastResult.score === textData.lastResult.total) ? 1 : 0,
    lastAttemptDate: textData.lastResult ? textData.lastResult.date : textData.createdAt,
    selectionMode: 'random'
  };

  return migratedData;
};

const getStoredTexts = (): TextData[] => {
  const texts = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');

  // Check if any migration is needed
  const needsMigration = texts.some((text: any) =>
    text.attempts === undefined ||
    text.attemptCount === undefined ||
    text.selectionMode === undefined
  );

  // Migrate old data format to new format
  const migratedTexts = texts.map(migrateOldData);

  // If migration occurred, save the migrated data
  if (needsMigration) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(migratedTexts));
  }

  // Sort by last attempt date (most recent first)
  return migratedTexts.sort((a: TextData, b: TextData) => {
    const dateA = new Date(a.lastAttemptDate || a.createdAt);
    const dateB = new Date(b.lastAttemptDate || b.createdAt);
    return dateB.getTime() - dateA.getTime();
  });
};

const getBestScore = (textData: TextData): { score: number; total: number } | null => {
  if (!textData.attempts || textData.attempts.length === 0) {
    return textData.lastResult ? { score: textData.lastResult.score, total: textData.lastResult.total } : null;
  }

  const bestAttempt = textData.attempts.reduce((best, current) => {
    const bestPercentage = (best.score / best.total) * 100;
    const currentPercentage = (current.score / current.total) * 100;
    return currentPercentage > bestPercentage ? current : best;
  });

  return { score: bestAttempt.score, total: bestAttempt.total };
};

const getLastScore = (textData: TextData): { score: number; total: number } | null => {
  if (!textData.attempts || textData.attempts.length === 0) {
    return textData.lastResult ? { score: textData.lastResult.score, total: textData.lastResult.total } : null;
  }

  const lastAttempt = textData.attempts[textData.attempts.length - 1];
  return { score: lastAttempt.score, total: lastAttempt.total };
};

type Step = 'input' | 'selection' | 'test';

function App(): React.JSX.Element {
  const [inputText, setInputText] = useState<string>('');
  const [originalWords, setOriginalWords] = useState<OriginalWord[]>([]);
  const [blankedText, setBlankedText] = useState<string>('');
  const [userAnswers, setUserAnswers] = useState<string[]>([]);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [results, setResults] = useState<Result[]>([]);
  const [currentStep, setCurrentStep] = useState<Step>('input');
  const [showAnswers, setShowAnswers] = useState<boolean>(false);
  const [storedTexts, setStoredTexts] = useState<TextData[]>([]);
  const [currentTextId, setCurrentTextId] = useState<string | null>(null);
  const [selectionMode, setSelectionMode] = useState<'random' | 'manual'>('random');
  const [selectedWordIndices, setSelectedWordIndices] = useState<number[]>([]);
  const [parsedWords, setParsedWords] = useState<string[]>([]);

  useEffect(() => {
    setStoredTexts(getStoredTexts());
  }, []);

  const proceedToSelection = useCallback(() => {
    if (!inputText.trim()) return;

    // Parse words for selection UI
    const words = inputText.trim().split(/([?!,;:.\s]+)/);
    setParsedWords(words);
    setCurrentStep('selection');
  }, [inputText]);

  const generateClozeTest = useCallback(() => {
    if (!inputText.trim()) return;

    try {
      let clozeResult;

      if (selectionMode === 'manual' && selectedWordIndices.length > 0) {
        // Use manual selection
        clozeResult = createClozeTest(inputText, {
          mode: 'manual',
          selectedIndices: selectedWordIndices
        });
      } else {
        // Use random selection
        clozeResult = createClozeTest(inputText);
      }

      const { originalWords: newOriginalWords, blankedText: newBlankedText } = clozeResult;

      setOriginalWords(newOriginalWords);
      setBlankedText(newBlankedText);
      setUserAnswers(new Array(newOriginalWords.length).fill(''));
      setCurrentStep('test');
      setShowResults(false);
      setResults([]);
      setShowAnswers(false);

      // Save text to localStorage if it's new
      if (!currentTextId) {
        const textId = saveTextToStorage(inputText, null, selectionMode, selectedWordIndices);
        setCurrentTextId(textId);
        setStoredTexts(getStoredTexts());
      }
    } catch (error) {
      alert(error instanceof Error ? error.message : 'An error occurred while generating the test');
    }
  }, [inputText, currentTextId, selectionMode, selectedWordIndices]);

  const handleAnswerChange = (answerIndex: number, value: string): void => {
    const newAnswers = [...userAnswers];
    newAnswers[answerIndex] = value;
    setUserAnswers(newAnswers);
  };

  const checkAnswers = (): void => {
    const stripPunctuation = (text: string) => text.replace(/[^\w\s]/g, '');

    const newResults: Result[] = originalWords.map((original, index) => {
      const userAnswer = stripPunctuation(userAnswers[index].trim().toLowerCase());
      const correctAnswer = stripPunctuation(original.word.toLowerCase());
      return {
        original: original.word,
        userAnswer: userAnswers[index],
        isCorrect: userAnswer === correctAnswer
      };
    });

    setResults(newResults);
    setShowResults(true);
    setShowAnswers(true);

    // Update localStorage with results if we have a textId
    if (currentTextId) {
      updateTextResult(currentTextId, newResults);
      setStoredTexts(getStoredTexts());
    }
  };

  const resetTest = (): void => {
    setInputText('');
    setOriginalWords([]);
    setBlankedText('');
    setUserAnswers([]);
    setShowResults(false);
    setResults([]);
    setCurrentStep('input');
    setShowAnswers(false);
    setCurrentTextId(null);
    setSelectionMode('random');
    setSelectedWordIndices([]);
    setParsedWords([]);
  };

  const loadStoredText = (textData: TextData): void => {
    setInputText(textData.text);
    setCurrentTextId(textData.id);
    setSelectionMode(textData.selectionMode || 'random');
    setSelectedWordIndices(textData.selectedWordIndices || []);

    // Skip selection page and go directly to generating the test for stored texts
    generateClozeTestFromStored(textData);
  };

  const generateClozeTestFromStored = useCallback((textData: TextData) => {
    const text = textData.text;
    const mode = textData.selectionMode || 'random';
    const selectedIndices = textData.selectedWordIndices || [];

    try {
      let clozeResult;

      if (mode === 'manual' && selectedIndices.length > 0) {
        clozeResult = createClozeTest(text, {
          mode: 'manual',
          selectedIndices: selectedIndices
        });
      } else {
        clozeResult = createClozeTest(text);
      }

      const { originalWords: newOriginalWords, blankedText: newBlankedText } = clozeResult;

      setOriginalWords(newOriginalWords);
      setBlankedText(newBlankedText);
      setUserAnswers(new Array(newOriginalWords.length).fill(''));
      setCurrentStep('test');
      setShowResults(false);
      setResults([]);
      setShowAnswers(false);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'An error occurred while generating the test');
    }
  }, []);

  const toggleWordSelection = (wordIndex: number): void => {
    // Switch to manual mode on first word click
    if (selectionMode === 'random') {
      setSelectionMode('manual');
    }

    const word = parsedWords[wordIndex];
    const selectableLength = 0;
    // Only allow selection of actual words (not punctuation/spaces)
    if (/^[\s,;.:!?]+$/.test(word) || word.length <= selectableLength) {
      return;
    }

    setSelectedWordIndices(prev => {
      if (prev.includes(wordIndex)) {
        return prev.filter(index => index !== wordIndex);
      } else {
        return [...prev, wordIndex];
      }
    });
  };

  const resetToRandomMode = (): void => {
    setSelectionMode('random');
    setSelectedWordIndices([]);
  };

  const renderBlankedText = (): React.JSX.Element[] => {
    const parts = blankedText.split(/____\d+____/);
    const elements: React.JSX.Element[] = [];

    parts.forEach((part, index) => {
      elements.push(<span key={`text-${index}`}>{part}</span>);

      if (index < parts.length - 1) {
        if (showAnswers && showResults) {
          const isCorrect = results[index]?.isCorrect;
          const userAnswer = results[index]?.userAnswer || '';
          const correctAnswer = originalWords[index]?.word || '';

          elements.push(
            <span
              key={`answer-${index}`}
              className={`answer-in-gap ${isCorrect ? 'correct' : 'incorrect'}`}
              title={!isCorrect ? `Correct answer: "${correctAnswer}"` : ''}
            >
              {isCorrect ? correctAnswer : <span className="strikethrough">{userAnswer}</span>}
              {!isCorrect && (
                <div className="hover-tooltip">
                  <span className="tooltip-label">Correct answer:</span>
                  <span className="tooltip-value correct-answer">"{correctAnswer}"</span>
                </div>
              )}
            </span>
          );
        } else {
          elements.push(
            <input
              key={`input-${index}`}
              type="text"
              value={userAnswers[index] || ''}
              onChange={(e) => handleAnswerChange(index, e.target.value)}
              className="blank-input"
              disabled={showResults}
            />
          );
        }
      }
    });

    return elements;
  };

  const correctCount = results.filter(result => result.isCorrect).length;
  const totalCount = results.length;

  return (
    <div className="App">
      <header className="App-header">
        <h1>Cloze Test</h1>

        {currentStep === 'input' && (
          <div className="input-section">
            {storedTexts.length > 0 && (
              <>
                <h2>Previously Stored Texts:</h2>
                <div className="stored-texts">
                  {storedTexts.map((textData) => {
                    const bestScore = getBestScore(textData);
                    const lastScore = getLastScore(textData);
                    const perfectCount = textData.perfectCount || 0;
                    const attemptCount = textData.attemptCount || 0;
                    const lastAttemptDate = textData.lastAttemptDate || textData.createdAt;

                    return (
                      <div key={textData.id} className="stored-text-item" onClick={() => loadStoredText(textData)}>
                        <div className="text-preview">{textData.preview}</div>
                        <div className="text-metadata">
                          <div className="metadata-row">
                            <span className="last-attempt-date">
                              Last tried: {new Date(lastAttemptDate).toLocaleDateString()}
                            </span>
                            <span className="attempt-count">
                              Attempts: {attemptCount}
                            </span>
                            <span className="selection-mode">
                              Mode: {textData.selectionMode === 'manual' ? 'Manual' : 'Random'}
                            </span>
                            <span className="perfect-count">
                              Perfect: {perfectCount}/3 ⭐
                            </span>
                          </div>
                          {(bestScore || lastScore) && (
                            <div className="scores-row">
                              {bestScore && (
                                <span className="best-score">
                                  Best: {bestScore.score}/{bestScore.total}
                                  ({Math.round((bestScore.score / bestScore.total) * 100)}%)
                                </span>
                              )}
                              {lastScore && (
                                <span className="last-score">
                                  Last: {lastScore.score}/{lastScore.total}
                                  ({Math.round((lastScore.score / lastScore.total) * 100)}%)
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <h2>Or paste new text:</h2>
              </>
            )}
            {storedTexts.length === 0 && <h2>Paste your text here:</h2>}
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Paste or type the text you want to create a cloze test from..."
              rows={10}
              cols={80}
            />
            <div>
              <button onClick={proceedToSelection} disabled={!inputText.trim()}>
                Continue
              </button>
            </div>
          </div>
        )}

        {currentStep === 'selection' && (
          <div className="input-section">
            <div className="mode-info">
              <h2>Select Words to Blank Out</h2>
              <div className="mode-instructions">
                <p>
                  <strong>Current Mode:</strong> {selectionMode === 'random' ? 'Random' : 'Manual'} Selection
                </p>
                {selectionMode === 'random' ? (
                  <p>
                    Words will be randomly selected each time you take the test.
                    <strong> Click on any word below to switch to Manual mode</strong> and choose specific words.
                  </p>
                ) : (
                  <p>
                    Click words to select/deselect them for blanking. Selected: {selectedWordIndices.length} words
                  </p>
                )}
              </div>
            </div>

            <div className="word-selection-area">
              {parsedWords.map((word, index) => {
                const selectableLength = 0;
                const isSelectable = !/^[\s,;.:!?]+$/.test(word) && word.length > selectableLength;
                const isSelected = selectedWordIndices.includes(index);

                return (
                  <span
                    key={index}
                    className={`word-item ${isSelectable ? 'selectable' : 'non-selectable'} ${isSelected ? 'selected' : ''}`}
                    onClick={() => isSelectable ? toggleWordSelection(index) : undefined}
                  >
                    {word}
                  </span>
                );
              })}
            </div>

            <div className="button-group">
              <button onClick={generateClozeTest} disabled={selectionMode === 'manual' && selectedWordIndices.length === 0}>
                Generate Test ({selectionMode === 'random' ? 'Random Words' : `${selectedWordIndices.length} Selected Words`})
              </button>
              {selectionMode === 'manual' && (
                <button onClick={resetToRandomMode} className="secondary">
                  Switch to Random Mode
                </button>
              )}
              <button onClick={() => setCurrentStep('input')} className="secondary">
                Back to Text Input
              </button>
            </div>
          </div>
        )}

        {currentStep === 'test' && (
          <div className="test-section">
            <h2>Fill in the blanks:</h2>
            <div className="blanked-text">
              {renderBlankedText()}
            </div>

            {!showResults && (
              <div className="button-group">
                <button onClick={checkAnswers}>
                  Show Results & Answers
                </button>
                <button onClick={resetTest} className="secondary">
                  Start Over
                </button>
              </div>
            )}

            {showResults && (
              <div className="results-section">
                <h3>Results: {correctCount}/{totalCount} correct ({Math.round((correctCount/totalCount) * 100)}%)</h3>
                <div className="results-list">
                  {results.map((result, index) => (
                    <div key={index} className={`result-item ${result.isCorrect ? 'correct' : 'incorrect'}`}>
                      <span className="label">Blank {index + 1}:</span>
                      <span className="answer">Your answer: "{result.userAnswer}"</span>
                      <span className="correct">Correct: "{result.original}"</span>
                      <span className="status">{result.isCorrect ? '✓' : '✗'}</span>
                    </div>
                  ))}
                </div>
                <button onClick={resetTest} className="primary">
                  Create New Test
                </button>
              </div>
            )}
          </div>
        )}
      </header>
    </div>
  );
}

export default App;
